import React from 'react';
import { useTranslation } from 'react-i18next';
import { QInputForm, QTooltip } from '@kubiq-base/core';
import { useFormContext } from 'react-hook-form';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface Props {
  translationNamespace: string;
  passwordValue?: string;
  passwordRepeatedValue?: string;
}

const IdenticalPasswordForm = ({ translationNamespace, passwordValue, passwordRepeatedValue }: Props): JSX.Element => {
  const { t } = useTranslation(translationNamespace);
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const validationRules = {
    required: 'requiredFieldMessage',
    pattern: {
      value:
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~])[A-Za-z\d !#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~]{10,}$/,
      message: 'invalidPassword',
    },
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <QInputForm
          control={control}
          name='password'
          label={t('password')}
          type='password'
          showPasswordToggle
          defaultValue={passwordValue as string}
          rules={validationRules}
          isError={errors.password && t(errors.password.message)}
        />

        <div style={{ position: 'absolute', right: 80, top: 26 }}>
          <QTooltip
            title={t('passwordReq')}
            arrow
            placement='left'
            // This is temporary, default props get overwritten this way.
            componentsProps={{
              arrow: { style: { marginLeft: '-0.65em' } },
              tooltip: { style: { whiteSpace: 'pre-line', fontSize: 12 } },
            }}
          >
            <HelpOutlineIcon fontSize='small' />
          </QTooltip>
        </div>
      </div>

      <QInputForm
        control={control}
        name='passwordRep'
        label={t('passwordRep')}
        type='password'
        showPasswordToggle
        defaultValue={passwordRepeatedValue as string}
        rules={{ ...validationRules, validate: (value) => value === getValues('password') }}
        isError={
          (errors.passwordRep && errors.passwordRep.type === 'validate' && t('notIdenticalPasswords')) ||
          (errors.passwordRep && t(errors.passwordRep.message))
        }
      />
    </>
  );
};

export default IdenticalPasswordForm;

// Old regex
// ^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W]){1,})(?!.*\s).{10,}$
//
// This regex will enforce these rules:
//
// At least one upper case English letter
// At least one lower case English letter
// At least one digit
// At least one special character
// Minimum 10 in length

// New regex
// ^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~])[A-Za-z\d !#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~]{10,}$
//
// This regex will enforce these rules:
//
// At least one upper case English letter A-Z
// At least one lower case English letter a-z
// At least one digit 0-9
// At least one special character !#$%&'()*+,-./:;<=>?@[\]^_`{|}~
// Minimum 10 in length
